import { logException } from 'approot/shared/debug';
import {
  localStorageWrapper,
  sessionStorageWrapper,
} from 'approot/shared/storage';
import type { StudentFragment } from '../../../students/students.graphql.generated';
import { PUBLIC_INTERACTIVE_BASE } from 'global/public-routes.constants';

// NOTE: this file is for the FE storage of logged in students, not class to do users

export const STUDENT_USER_DATA_KEY = `studentUserData:${process.env.REACT_APP_REGION}`;
export const STUDENT_CLASS_INFO_KEY = `studentClassInfo:${process.env.REACT_APP_REGION}`;
export type StorageStudentUserData = {
  token: string;
  studentUser: StudentFragment;
  classId?: number;
  classToDoId?: string;
};

// we have to redeclare the SIGN_IN_REDIRECTION_PATH_KEY here because importing for session storage causes a circular dependency
const SIGN_IN_REDIRECTION_PATH_KEY = 'signinRedirectionPath';

export function getStudentUserData() {
  let studentUserData: StorageStudentUserData | undefined;
  const data = localStorageWrapper.getItem(STUDENT_USER_DATA_KEY);

  if (data) {
    try {
      studentUserData = JSON.parse(data);
    } catch (e) {
      logException({
        sourceFile: 'auth.tsx',
        message: `Invalid student user data: ${data}`,
        e,
      });
    }
  }

  return studentUserData;
}

export function getStudentUserId() {
  const studentUserData = getStudentUserData();
  return studentUserData?.studentUser.id;
}

export function setStudentUserData(student: StorageStudentUserData) {
  const studentUserDataString = JSON.stringify(student);
  localStorageWrapper.setItem(STUDENT_USER_DATA_KEY, studentUserDataString);
}

export const clearStudentUserData = () => {
  localStorageWrapper.removeItem(STUDENT_USER_DATA_KEY);
};

export type StudentClassInfo = {
  classId: number;
  classToDoId: string;
};

// Student class info remains in session storage to allow for
// easy signing back in to previous classes
export function setStudentClassInfo(classInfo: StudentClassInfo) {
  clearStudentInteractiveSigninRedirectionPathForSession();
  sessionStorageWrapper.setItem(
    STUDENT_CLASS_INFO_KEY,
    JSON.stringify(classInfo)
  );
}

export function getStudentClassInfo(): StudentClassInfo | undefined {
  const classInfo = sessionStorageWrapper.getItem(STUDENT_CLASS_INFO_KEY);
  return classInfo ? JSON.parse(classInfo) : undefined;
}

export function clearStudentClassInfo() {
  sessionStorageWrapper.removeItem(STUDENT_CLASS_INFO_KEY);
}

export function clearStudentInteractiveSigninRedirectionPathForSession() {
  const redirectPath = sessionStorageWrapper.getItem(
    SIGN_IN_REDIRECTION_PATH_KEY
  );
  if (redirectPath && redirectPath.includes(PUBLIC_INTERACTIVE_BASE)) {
    sessionStorageWrapper.removeItem(SIGN_IN_REDIRECTION_PATH_KEY);
  }
}
